import type { CalAttrs } from './types.js';

/**
 * Parse data-* attributes from a [data-cal] or [data-cal-inline] element.
 *
 * Supported attributes:
 *   data-cal="mj/30min"                    — booking path (required)
 *   data-cal-inline="mj/30min"             — same but for inline mode (read by index.ts)
 *   data-cal-theme="dark"|"light"
 *   data-cal-prefill-email="user@x.com"
 *   data-cal-prefill-name="Ana García"
 *   data-cal-locale="es"|"en"
 *   data-cal-color="#1A365D"               — override brand primary color
 *
 * Path formats accepted:
 *   mj/30min                               — alias/event
 *   t/abemon-sales/30min                   — team/event
 *   p/aB3cD9                               — propose-times smart link
 *   o/xY4zT2                               — one-off smart link
 */
export function parseDataAttrs(el: Element): CalAttrs | null {
  const rawPath =
    el.getAttribute('data-cal') ??
    el.getAttribute('data-cal-inline') ??
    '';

  const path = rawPath.trim();
  if (!path) {
    console.warn('[cal] Element missing booking path:', el);
    return null;
  }

  const theme = el.getAttribute('data-cal-theme') ?? undefined;
  const prefillEmail = el.getAttribute('data-cal-prefill-email') ?? undefined;
  const prefillName = el.getAttribute('data-cal-prefill-name') ?? undefined;
  const locale = el.getAttribute('data-cal-locale') ?? undefined;
  const color = el.getAttribute('data-cal-color') ?? undefined;

  return {
    path,
    theme: theme === 'dark' || theme === 'light' ? theme : undefined,
    prefillEmail,
    prefillName,
    locale,
    color,
  };
}

/**
 * Build the iframe src URL for a given path and base URL.
 *
 * Appends ?embed=1 so the landing page renders iframe-friendly chrome.
 * Also forwards theme, locale, prefill params as query params for SSR.
 */
export function buildIframeSrc(
  apiBaseUrl: string,
  attrs: CalAttrs,
): string {
  // apiBaseUrl e.g. "https://cal.bm.consulting"
  // path e.g. "mj/30min"
  const base = apiBaseUrl.replace(/\/$/, '');
  const url = new URL(`${base}/${attrs.path}`);

  url.searchParams.set('embed', '1');

  if (attrs.theme) url.searchParams.set('theme', attrs.theme);
  if (attrs.locale) url.searchParams.set('locale', attrs.locale);
  if (attrs.prefillEmail) url.searchParams.set('email', attrs.prefillEmail);
  if (attrs.prefillName) url.searchParams.set('name', attrs.prefillName);

  return url.toString();
}
